import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import * as icons from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import * as Form from '@/components/Form';
import { Icon } from '@/components/Icon';
import * as infsearch from '@/components/InfiniteSearchScrollable';
import SearchInput from '@/components/SearchInput';
import * as models from '@/models';
import * as utils from '@/utils';
/** An infinite-scrolling list component pre-configured for courses. */
const CourseList = infsearch.create('courses');
/** A component for searching and selecting a course from a list. */
export default tsx.componentFactoryOf().create({
    name: 'course-select',
    props: {
        /** A course to omit from the selectable list (e.g., the current course). */
        omit: p(api.Course).optional,
        /** The currently selected course. */
        selected: p.ofType().required,
    },
    model: {
        prop: 'selected',
        event: 'change',
    },
    /** @returns The initial data for the component. */
    data() {
        return { query: '' };
    },
    computed: {
        /** @returns A `CourseSearcher` instance for the course list. */
        searcher() {
            return new models.CourseSearcher();
        },
    },
    methods: {
        /**
         * Toggles the selection of a course.
         *
         * @param course The course that was clicked.
         */
        selectCourse(course) {
            if (this.selected.extract() === course) {
                tsx.emitOn(this, 'onChange', Maybe.empty());
            }
            else {
                tsx.emitOn(this, 'onChange', Maybe.of(course));
            }
        },
        /**
         * Renders a single selectable course item.
         *
         * @param course The course to render.
         * @returns The VNode for the course item.
         */
        renderCourse(course) {
            const isSelected = this.selected.extract() === course;
            return utils.ifOrEmpty(course.id !== this.omit?.id, () => (<button role="radio" aria-checked={isSelected} type="button" class={[
                    'cg-btn rounded-none text-base justify-start gap-3 focus:outline-none text-left',
                    'border-l-0 border-r-0',
                    'w-full p-3!',
                    {
                        'btn-neutral-1 focus:bg-actionable-2': !isSelected,
                        'btn-actionable-3 font-semibold': isSelected,
                    },
                ]} onClick={() => this.selectCourse(course)}>
                    <Icon icon={icons.Check} class={[
                    'transition duration-100',
                    { 'opacity-0': !isSelected, 'opacity-100': isSelected },
                ]}/>
                    <span class="text-truncate">{course.name}</span>
                </button>));
        },
        /**
         * Renders the scrollable container for the list of courses.
         *
         * @param courses The list of courses to render.
         * @param loadMoreButton The VNode for the "Load More" button.
         * @returns The VNode for the course list container.
         */
        renderCourses(courses, loadMoreButton) {
            return (<div class={[
                    'max-h-60 overflow-auto',
                    'bg-white dark:bg-transparent',
                    'border border-neutral-10 dark:border-neutral-80',
                    'divide-y divide-neutral-10 dark:divide-neutral-80',
                    'rounded',
                ]}>
                    {courses.map(c => this.renderCourse(c))}
                    {loadMoreButton}
                </div>);
        },
    },
    /**
     * @returns The VNode for the component.
     */
    render() {
        return (<CourseList class="@container" searcher={this.searcher} query={this.query} scopedSlots={{
                body: ({ items, statusNode, loadMoreButton }) => [
                    <div class="form-layout-row mb-3!">
                            <Form.Field>
                                <Form.Label>Search course</Form.Label>
                                <SearchInput resource="course" query={this.query} onInput={({ query }) => {
                            this.query = query ?? '';
                        }}/>
                            </Form.Field>
                        </div>,
                    statusNode,
                    items.length > 0 && this.renderCourses(items, loadMoreButton),
                ],
                noItemsFound: ({ searching }) => searching
                    ? 'No courses match your query.'
                    : 'No courses available for import.',
            }}/>);
    },
});
